
import * as React from "react";
import FilterableCommandBarItem, { DisplayOption, IFilterableCommandBarItem } from "./FilterableCommandBarItem";

/**
 * Adapter class that wraps FilterableCommandBarItem with FluentUI 2 Toolbar conversion capabilities.
 * Provides object-oriented conversion logic for migrating from CommandBar to Toolbar components.
 */
export default class FilterableCommandBarItemV2 {
    private _legacyItem: FilterableCommandBarItem;

    /**
     * Private constructor - use create() factory method instead
     * @param legacyItem - The wrapped FilterableCommandBarItem instance
     */
    constructor(legacyItem: FilterableCommandBarItem) {
        this._legacyItem = legacyItem;
    }

    /**
     * Static factory method to create a FilterableCommandBarItemV2
     * @param props - IFilterableCommandBarItem configuration
     * @returns FilterableCommandBarItemV2 instance wrapping a legacy item
     */
    public static create(props: IFilterableCommandBarItem): FilterableCommandBarItemV2 {
        const legacyItem = FilterableCommandBarItem.create(props);
        return new FilterableCommandBarItemV2(legacyItem);
    }

    /**
     * Converts the legacy item to a FluentUI 2 Toolbar item.
     * Since ScheduleGenerationEditorPage uses onRender for all items, this method
     * wraps the custom render result in a div with proper React key.
     * @returns JSX.Element suitable for use in FluentUI 2 Toolbar
     */
    public toToolbarItem(): JSX.Element {
        const { onRender, key } = this._legacyItem;

        if (!onRender) {
            throw new Error(`FilterableCommandBarItemV2 with key "${key}" requires onRender function for Toolbar conversion`);
        }

        // Wrap custom render result in div with proper key for React reconciliation
        return (<div key={key}>
                {onRender(this._legacyItem) as JSX.Element}
            </div>
        );
    }

    /**
     * Gets the display option (Left/Right/Overflow) for positioning in Toolbar
     * @returns DisplayOption for determining which ToolbarGroup to render in
     */
    public get displayOption(): DisplayOption {
        return this._legacyItem.displayOption || DisplayOption.Left;
    }

    /**
     * Gets the unique key for this item
     * @returns string key for React reconciliation
     */
    public get key(): string {
        return this._legacyItem.key;
    }

    /**
     * Gets the underlying legacy item for compatibility scenarios
     * @returns FilterableCommandBarItem instance
     */
    public get legacyItem(): FilterableCommandBarItem {
        return this._legacyItem;
    }
}

// Re-export DisplayOption enum for convenience
export { DisplayOption };